import { setToken } from 'store/slices/auth'
import { setUser } from 'store/slices/user'
import {
  UserSignIn,
  UserSignUp,
  UserSignInResponse,
  PurposeType,
  VerifyUserAction,
  OTPResponse,
  BaseError,
} from 'types'
import { ResetPasswordModel } from 'types/ResetPassword'
import { getDeviceData } from 'utils/getDeviceData'
import { emptySplitApi } from './emptySplit.api'
import { toast } from 'components/FSGToast'
import { TOAST_ERROR_TIMEOUT } from 'utils/constants'

// Define a service using a base URL and expected endpoints
export const authApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    signIn: builder.mutation<UserSignInResponse, UserSignIn>({
      query: (credentials) => ({
        url: 'auth/login',
        method: 'POST',
        body: {
          ...credentials,
          device: getDeviceData(),
        },
        responseHandler: (response) => {
          return response.json()
        },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          dispatch(setToken(data.accessToken))
          dispatch(setUser(data.user))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    signUp: builder.mutation<OTPResponse, UserSignUp>({
      query: (credentials) => ({
        url: 'auth/register',
        method: 'POST',
        body: {
          ...credentials,
          device: getDeviceData(),
        },
        responseHandler: (response) => {
          return response.json()
        },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
        } catch (err) {
          // `onError` side-effect
          toast({
            title: 'Sign up error',
            description:
              (err as BaseError).error?.data?.message ||
              'Server error, please contact administrator',
            status: 'error',
            duration: TOAST_ERROR_TIMEOUT,
            isClosable: true,
            position: 'top',
          })
        }
      },
    }),
    forgotPassword: builder.mutation<any, { email: string }>({
      query: (body) => ({
        url: 'auth/forgot-password',
        method: 'POST',
        body: {
          ...body,
          purpose: PurposeType.FORGOT_PASSWORD,
        },
        responseHandler: (response) => {
          return response.json()
        },
      }),
    }),
    verifySignUp: builder.mutation<
      UserSignInResponse,
      { userId: string; verificationCode: string }
    >({
      query: (body) => ({
        url: 'auth/users/verify',
        method: 'POST',
        body,
        responseHandler: (response) => {
          return response.json()
        },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          dispatch(setToken(data.accessToken))
          dispatch(setUser(data.user))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    verifyForgotPassword: builder.mutation<
      { token: string },
      { email: string; verificationCode: string }
    >({
      query: (body) => ({
        url: 'auth/verify/password-code',
        method: 'POST',
        body,
        responseHandler: (response) => {
          return response.json()
        },
      }),
    }),
    resetPassword: builder.mutation<any, ResetPasswordModel>({
      query: (body) => ({
        url: 'auth/users/reset-password',
        method: 'POST',
        body,
        responseHandler: (response) => {
          return response.json()
        },
      }),
    }),
    resendVerificationCode: builder.mutation<any, { userId: string; purpose: PurposeType }>({
      query: (body) => ({
        url: 'auth/otp/resend',
        method: 'POST',
        body,
        responseHandler: (response) => {
          return response.json()
        },
      }),
    }),
    verifyOtp: builder.mutation<any, VerifyUserAction>({
      query: (body) => ({
        url: 'auth/users/verify',
        method: 'POST',
        body,
        responseHandler: (response) => {
          return response.json()
        },
      }),
    }),
    logout: builder.mutation<any, void>({
      query: () => ({
        url: 'auth/logout',
        method: 'POST',
        responseHandler: (response) => {
          return response.json()
        },
      }),
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useSignInMutation,
  useSignUpMutation,
  useForgotPasswordMutation,
  useVerifySignUpMutation,
  useVerifyForgotPasswordMutation,
  useResendVerificationCodeMutation,
  useResetPasswordMutation,
  useVerifyOtpMutation,
  useLogoutMutation,
} = authApi
